/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import React, { useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import serverRenderingUtils from '../../../../../helpers/serverRenderingUtils';
import { EmbeddedYoutubeVideo } from '../../../../components/EmbeddedYoutubeVideo';
import { useBrowserSize } from '../../../../contexts/BrowserSize';
import { MD_SCREEN_MIN, SM_SCREEN_MAX } from '../../../../modules/Styleguide';
import { CDN_PATH } from '../constants';
import { Section, SectionContent, SectionHeader } from '../styled';
import {
  TranscribedVideoSquareIcon,
  TranscribedVideoStoriesIcon,
  TranscribedVideoWideIcon,
} from './CreatorToolsSvgs';

enum SectionId {
  RECORD_WITH_FRIENDS = 'record-with-friends',
  TRANSCRIBED_VIDEO = 'transcribed-video',
  COVER_ART_CREATOR = 'cover-art-creator',
  VOICE_MESSAGES = 'voice-messages',
}

enum TranscribedVideoSizes {
  SQUARE = 'square',
  STORIES = 'stories',
  WIDE = 'wide',
}

export function MoreCreatorToolsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  const [activeSection, setActiveSection] = React.useState<SectionId | null>(
    null
  );

  const [gifSize, setGifSize] = useState<TranscribedVideoSizes>(
    TranscribedVideoSizes.SQUARE
  );
  const { width } = useBrowserSize();
  const isMobileBreakpoint = width <= MD_SCREEN_MIN;

  const [showVideo, toggleVideo] = useState(false);

  function showVideoPlayer() {
    return showVideo ? (
      <EmbeddedYoutubeVideo
        title="Remote podcasting just got easier: Introducing Record With Friends 2.0 from Anchor"
        youtubeVideoId="fX4KBl4R4GY"
        paddingBottomOffset="56.25%"
        youtubeEmbedParameters={{
          autoplay: 1,
          controls: 1,
          modestbranding: 1,
          loop: 1,
        }}
      />
    ) : (
      <button type="button" onClick={() => toggleVideo(true)}>
        <picture>
          <source
            type="image/webp"
            srcSet={`${CDN_PATH}/record-with-friends-video.webp`}
          />
          <img
            // @ts-ignore;
            loading="lazy"
            width="100%"
            alt="Record with friends video thumbnail"
            src={`${CDN_PATH}/record-with-friends-video.jpeg`}
          />
        </picture>
      </button>
    );
  }

  function handleInView(
    inView: boolean,
    sectionId: SectionId,
    videoShowing: boolean
  ) {
    if (inView && !isMobileBreakpoint) {
      if (videoShowing && sectionId !== SectionId.RECORD_WITH_FRIENDS) {
        toggleVideo(false);
      }
      setActiveSection(sectionId);
    }
  }

  return (
    <Section
      ref={sectionRef}
      css={css`
        background: white;
      `}
    >
      <SectionContent
        css={css`
          align-items: flex-start;
        `}
      >
        <div
          css={css`
            @media (min-width: ${MD_SCREEN_MIN}px) {
              width: 50%;
            }
          `}
        >
          <InView
            rootMargin="-40% 0px -40% 0px"
            threshold={0.25}
            onChange={inView =>
              handleInView(inView, SectionId.RECORD_WITH_FRIENDS, showVideo)
            }
          >
            <StyledInView>
              <SectionHeader>Record with friends</SectionHeader>
              <p>
                Record remotely with guests or co-hosts, wherever they are in
                the world. Multiple people can join at a time from web or
                mobile—no app or account required.
              </p>
              <MobileImgContainer>
                {isMobileBreakpoint && showVideoPlayer()}
              </MobileImgContainer>
            </StyledInView>
          </InView>

          <InView
            rootMargin="-40% 0px -40% 0px"
            threshold={0.25}
            onChange={inView =>
              handleInView(inView, SectionId.VOICE_MESSAGES, showVideo)
            }
          >
            <StyledInView>
              <SectionHeader>Voice messages</SectionHeader>
              <p>
                Engage with your listeners by inviting them to leave you a voice
                message. Add your favorites to your next episode with a single
                tap.
              </p>
              <MobileImgContainer>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${CDN_PATH}/voice-messages-screen.webp`}
                  />
                  <img
                    // @ts-ignore;
                    loading="lazy"
                    width="100%"
                    alt="Voice messages screen on Anchor app"
                    src={`${CDN_PATH}/voice-messages-screen.png`}
                  />
                </picture>
              </MobileImgContainer>
            </StyledInView>
          </InView>

          <InView
            rootMargin="-40% 0px -40% 0px"
            threshold={0.25}
            onChange={inView =>
              handleInView(inView, SectionId.TRANSCRIBED_VIDEO, showVideo)
            }
          >
            <StyledInView>
              <SectionHeader>Transcribed video</SectionHeader>
              <p>
                Transform audio clips into animated videos that are ready for
                sharing.
              </p>
              <p
                css={css`
                  margin-top: 20px;
                `}
              >
                Available in 3 sizes to fit your favorite social platform.
              </p>
              <MobileImgContainer
                css={css`
                  height: 284px;
                  text-align: center;
                  display: flex;
                  justify-content: center;
                `}
              >
                {serverRenderingUtils.isIOS() ? (
                  <img
                    css={css`
                      max-width: 320px;
                      max-height: 284px;
                      width: auto;
                      height: auto;
                      align-self: center;
                    `}
                    // @ts-ignore;
                    loading="lazy"
                    width="100%"
                    alt="transcribed video"
                    src={`${CDN_PATH}/transcribed-video-${gifSize}-mobile.gif`}
                  />
                ) : (
                  <video
                    css={css`
                      height: 284px;
                    `}
                    src={`${CDN_PATH}/transcribed-video-${gifSize}-mobile.mp4`}
                    width="100%"
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                  />
                )}
              </MobileImgContainer>

              <TranscribedVideoButtonContainer>
                <button
                  onClick={() => setGifSize(TranscribedVideoSizes.SQUARE)}
                >
                  <TranscribedVideoSquareIcon
                    isActive={gifSize === TranscribedVideoSizes.SQUARE}
                  />
                  <span>Square</span>
                </button>
                <button onClick={() => setGifSize(TranscribedVideoSizes.WIDE)}>
                  <TranscribedVideoWideIcon
                    isActive={gifSize === TranscribedVideoSizes.WIDE}
                  />
                  <span>Wide</span>
                </button>
                <button
                  onClick={() => setGifSize(TranscribedVideoSizes.STORIES)}
                >
                  <TranscribedVideoStoriesIcon
                    isActive={gifSize === TranscribedVideoSizes.STORIES}
                  />
                  <span>Stories</span>
                </button>
              </TranscribedVideoButtonContainer>
            </StyledInView>
          </InView>

          <InView
            rootMargin="-50% 0px -50% 0px"
            onChange={inView =>
              handleInView(inView, SectionId.COVER_ART_CREATOR, showVideo)
            }
          >
            <StyledInView
              css={css`
                @media (min-width: ${MD_SCREEN_MIN}px) {
                  padding-bottom: 200px;
                }
              `}
            >
              <SectionHeader>Cover art creator</SectionHeader>
              <p>
                Generate custom cover art in seconds using images and fonts that
                will help your podcast look as great as it sounds. Read more
                about designing your own{' '}
                <a
                  href="https://medium.com/anchor/the-dos-and-donts-of-podcast-cover-art-a0e8a3b51ee7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <MobileImgContainer>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${CDN_PATH}/coverart-creator.webp`}
                  />
                  <img
                    // @ts-ignore;
                    loading="lazy"
                    width="100%"
                    alt="cover art creator screen in Anchor app"
                    src={`${CDN_PATH}/coverart-creator.jpg`}
                  />
                </picture>
              </MobileImgContainer>
            </StyledInView>
          </InView>
        </div>
        <ScrollingStickyContainer>
          <FadeInOut inView={activeSection === SectionId.RECORD_WITH_FRIENDS}>
            {!isMobileBreakpoint && showVideoPlayer()}
          </FadeInOut>
          <FadeInOut inView={activeSection === SectionId.VOICE_MESSAGES}>
            <picture
              css={css`
                margin-top: -30px;
              `}
            >
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/voice-messages-screen.webp`}
              />
              <img
                // @ts-ignore;
                loading="lazy"
                width="100%"
                alt="Voice messages screen in Anchor app"
                src={`${CDN_PATH}/voice-messages-screen.png`}
              />
            </picture>
          </FadeInOut>
          <FadeInOut
            inView={
              activeSection === SectionId.TRANSCRIBED_VIDEO &&
              gifSize === TranscribedVideoSizes.SQUARE
            }
          >
            <video
              css={css`
                width: 360px;
              `}
              autoPlay={true}
              loop={true}
              muted={true}
              playsInline={true}
            >
              <source
                src={`${CDN_PATH}/transcribed-video-square.webm`}
                type="video/webm"
              />
              <source
                src={`${CDN_PATH}/transcribed-video-square.mp4`}
                type="video/mp4"
              />
            </video>
          </FadeInOut>
          <FadeInOut
            inView={
              activeSection === SectionId.TRANSCRIBED_VIDEO &&
              gifSize === TranscribedVideoSizes.WIDE
            }
          >
            <video
              width="100%"
              autoPlay={true}
              loop={true}
              muted={true}
              playsInline={true}
            >
              <source
                src={`${CDN_PATH}/transcribed-video-wide.webm`}
                type="video/webm"
              />
              <source
                src={`${CDN_PATH}/transcribed-video-wide.mp4`}
                type="video/mp4"
              />
            </video>
          </FadeInOut>
          <FadeInOut
            inView={
              activeSection === SectionId.TRANSCRIBED_VIDEO &&
              gifSize === TranscribedVideoSizes.STORIES
            }
          >
            <video
              css={css`
                width: 270px;
                margin-top: -70px;
              `}
              autoPlay={true}
              loop={true}
              muted={true}
              playsInline={true}
            >
              <source
                src={`${CDN_PATH}/transcribed-video-stories.webm`}
                type="video/webm"
              />
              <source
                src={`${CDN_PATH}/transcribed-video-stories.mp4`}
                type="video/mp4"
              />
            </video>
          </FadeInOut>
          <FadeInOut inView={activeSection === SectionId.COVER_ART_CREATOR}>
            <picture>
              <source
                type="image/webp"
                srcSet={`${CDN_PATH}/coverart-creator.webp`}
              />
              <img
                // @ts-ignore;
                loading="lazy"
                width="100%"
                alt="cover art creator screen in Anchor app"
                src={`${CDN_PATH}/coverart-creator.jpg`}
              />
            </picture>
          </FadeInOut>
        </ScrollingStickyContainer>
      </SectionContent>
    </Section>
  );
}

type FadeInOutProps = {
  inView: boolean;
};

const FadeInOut = styled.div<FadeInOutProps>`
  position: absolute;
  width: 100%;
  text-align: center;
  z-index: ${({ inView }: FadeInOutProps) => (inView ? '1' : '0')};
  opacity: ${({ inView }: FadeInOutProps) => (inView ? '1' : '0')};
  transition: opacity ease-in 0.2s;
`;

const StyledInView = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 100px 0;
  @media (min-width: ${MD_SCREEN_MIN}px) {
    max-width: 300px;
    padding-top: 200px;
    h2 {
      margin-bottom: 30px;
    }
  }
`;

const MobileImgContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 360px;
  align-self: center;
  margin-top: 30px;
  @media (min-width: ${MD_SCREEN_MIN}px) {
    visibility: hidden;
    display: none;
  }
`;
const TranscribedVideoButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  button {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    width: 100px;
    height: 100px;
  }
  button span {
    margin-top: 10px;
  }
`;

const ScrollingStickyContainer = styled.div`
  position: sticky;
  top: 154px;
  width: 50%;
  height: calc(100vh - 154px);
  display: flex;
  align-items: center;
  @media (max-width: ${SM_SCREEN_MAX}px) {
    visibility: hidden;
    display: none;
  }
`;
